import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useInterval } from 'usehooks-ts';
import { Spin } from 'antd';
import Breadcrumb from 'components/atoms/Breadcrumb';
import TabsContainer from 'components/molecules/PricingSimulator/TabsContainer';
import { pricingSimulatorDataContext } from 'contexts/PricingSimulatorContext';
import ProjectOverviewContainer from 'components/molecules/PricingSimulator/ScenarioOverviewContainer';
import Footer from 'components/molecules/PricingSimulator/Footer';
import DimSection from 'components/molecules/PricingSimulator/DimSection';
import LenseSection from 'components/molecules/PricingSimulator/LenseSection';
import {
    calculateContainerHeight,
    formatDatePreservingTimeValues,
    formatString,
    generateErrorMsg,
    getConfig,
    sendNotification,
} from 'utils/helperMethods';
import { useConvertPercentageToPxWidth, useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';
import InputDisplay from 'components/atoms/DisplayName/input-display';
import {
    PricingSimulatorNotificationMessages,
    priceSimulatorErrorMessages,
    pricingSimulatorErrorMessages,
} from 'utils/Enums';
import { AttributeTypeItem, Project, Scenario } from 'types/pricingSimulator';
import { useNavigate, useParams } from 'react-router-dom';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { pricingSimulatorUrls } from 'config/urls';
import { FilterContextData } from 'contexts/FilterContext';
import dayjs from 'dayjs';
import { UserContext } from 'contexts/ConfigContext';
import ScenarioActions from 'components/molecules/PricingSimulator/ScenarioActions';
import _ from 'lodash';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import Loader from 'components/molecules/Loader';
import useFetchScenarioStatus from 'hooks/pricingSimulator/scenario/useFetchScenarioStatus';
import { PricingSimulatorQueryEnum } from 'utils/queryKeysEnum';
import ScenarioTableWrapper from 'components/molecules/PricingSimulator/ScenarioTableWrapper';
import { FileAddOutlined } from '@ant-design/icons';
import { queryClient } from 'services/reactQueryService';
import FilterRibbon from 'components/molecules/PricingSimulator/FilterRibbon/FilterRibbon';
import { useFetchScenarioDetails } from 'hooks/pricingSimulator/useFetchScenarioDetails';
import { pricingSimulatorWrapperDataContext } from 'contexts/PricingSimulatorWrapperContext';
import ScenarioTag from './ScenarioTag';

const chartHeight = calculateContainerHeight(1, 16 * 3);
const defaultDimSectionWidth = 25;

export const pollingStatuses = ['IN_PROGRESS', 'SETUP'];
export const errorStatuses = ['ERROR', 'INVALID'];

const UpdateOrViewPricingSimulator = () => {
    const { mode, id, projectId: projectIdFromUrl, scenarioId: scenarioIdFromUrl } = useParams();

    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const pricingSimulatorConfig = getConfig('pricingSimulator', appConfig);
    const projectStatuses = pricingSimulatorConfig?.projectStatuses;
    const dataSourceList = pricingSimulatorConfig?.projectDataSource;
    const scenarioPerProjectLimit = pricingSimulatorConfig?.scenarioPerProjectLimit;

    //simulator wrapper context
    const { dimensionConfigData } = useContext(pricingSimulatorWrapperDataContext);
    const dim1DropdownList = dimensionConfigData?.dim1DropdownList ?? [];
    const defaultDim1DropdownItem = dimensionConfigData?.defaultDim1DropdownItem ?? {
        value: '',
        label: '',
        dimension: '',
    };
    const dim1Attribute = dimensionConfigData?.dim1Attribute;
    const dim2Attribute = dimensionConfigData?.dim2Attribute;

    //filter context
    const { allDimAndAttributeTypeList, timePeriodSelection, timePeriodLoading } = useContext(FilterContextData);
    const enabledVariable = Boolean(
        allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !timePeriodLoading &&
            timePeriodSelection,
    );
    const navigate = useNavigate();

    const {
        breadcrumbItems,
        selectedScenarioInfo,
        updateScenarioProperty,
        scenarioId,
        setDimensionCheckboxObj,
        lenseConfigLoading,
        refetchLensConfigData,
        scenarioDataLoading,
        refetchScenarioTableData,
        setSelectedProjectInfo,
        updateSelectedScenarioId,
        selectedProjectInfo,
        loggedInUser,
        viewMode,
        selectedDimensionListValues,
        pricingSimulatorIndexScreenUrl,
        showIsEditedRecords,
        setShowIsEditedRecords,
        allCheckedAttribute,
        setAllCheckedAttribute,
        allSelectedValueForAllAttribute,
        setAllSelectedValueForAllAttribute,
        applyFilterObject,
        setApplyFilterObject,
        resetFilterObject,
        saveFilterConfig,
        hasProjectAccess,
        projectAccessLoading,
    } = useContext(pricingSimulatorDataContext);

    const [tempName, setTempName] = useState(''); // to track new scenario name
    const [shouldFetch, setShouldFetch] = useState(true); // SHORT POLLING

    //get single project details
    const { loading: singleProjectDetailsLoading } = useCommonApiHandler({
        method: 'get',
        queryConfig: { enabled: Boolean(hasProjectAccess) && Boolean(dim1Attribute) && Boolean(enabledVariable) },
        queryKey: ['ps-single-project-details', projectIdFromUrl],
        url: `${pricingSimulatorUrls.getSingleProjectDetailsUrl}/${projectIdFromUrl}`,
        variables: {},
        parserFunction: (res: any) => {
            //redirect user to index page if error is thrown
            if (!res || res?.error || Object.keys(res).length === 0) {
                sendNotification('error', 'Project does not exist.');
                navigate(pricingSimulatorIndexScreenUrl);
                throw 'No Data';
            }
            const dim1Parsed = res?.project_filters?.dim1;
            const dim2Parsed = res?.project_filters?.dim2;
            const { start_timestamp, end_timestamp } = res?.simulation_time_period;
            const formattedSimulationStartDate = formatDatePreservingTimeValues(start_timestamp);
            const formattedSimulationEndDate = formatDatePreservingTimeValues(end_timestamp);
            const formattedPriceEffectiveEndDate = formatDatePreservingTimeValues(res?.price_effective_date);
            setSelectedProjectInfo((prevScenarioInfo: Project | null) => {
                const parsedData = {
                    projectName: res?.project_name,
                    projectId: res?.project_id,
                    createdBy: res?.created_by,
                    status: res?.status,
                    dataSource: dataSourceList?.find((item: any) => item?.value === res?.data_source)?.label,
                    dim1Filter: dim1Attribute
                        ? {
                              attributeType: dim1Attribute?.label,
                              attributeNames: formatString(dim1Parsed?.[dim1Attribute?.['key']]?.join(', ') || '', 15),
                              fullAttributeNames: dim1Parsed?.[dim1Attribute?.['key']]?.join(', '),
                          }
                        : null,
                    dim2Filter: dim2Attribute
                        ? {
                              attributeType: dim2Attribute?.label,
                              attributeNames: formatString(dim2Parsed?.[dim2Attribute?.['key']]?.join(', ') || '', 15),
                              fullAttributeNames: dim2Parsed?.[dim2Attribute?.['key']]?.join(', '),
                          }
                        : null,
                    lastUpdated: dayjs.unix(res?.updated_at).fromNow(),
                    simulationPeriod: `${dayjs(formattedSimulationStartDate)
                        .add(6, 'day')
                        .format(commonConfig?.dateFormatAlt)} - ${dayjs(formattedSimulationEndDate)
                        .add(6, 'day')
                        .format(commonConfig?.dateFormatAlt)}`,
                    priceEffectiveDate:
                        res?.price_effective_date &&
                        dayjs(formattedPriceEffectiveEndDate).add(6, 'day').format(commonConfig?.dateFormatAlt),
                    simulationList: prevScenarioInfo?.simulationList || [],
                    isPrivate: res?.is_private,
                };
                return parsedData;
            });

            return {};
        },
    });

    //get all scenarios under same project
    const { loading: allScenariosLoading } = useCommonApiHandler({
        method: 'get',
        queryConfig: { enabled: Boolean(hasProjectAccess) && Boolean(enabledVariable) },
        queryKey: ['ps-single-project-details', 'ps-all-scenarios-details', projectIdFromUrl],
        url: `${pricingSimulatorUrls.getAllScenariosUrl}/${projectIdFromUrl}/scenarios`,
        variables: {},
        parserFunction: (res: any) => {
            //error handling pending
            if (res?.error) {
                return {};
            }
            try {
                setSelectedProjectInfo((prevScenarioInfo: Project | null) => {
                    let tempScenarioInfo = prevScenarioInfo || {};
                    const scenariosList = res?.map((item: any) => {
                        return {
                            scenarioName: item?.name,
                            scenarioId: item?.scenario_id,
                            projectId: item?.project_id,
                            createdBy: item?.created_by,
                            updatedAt: dayjs.unix(item?.updated_at).fromNow(),
                            status: item?.status,
                        };
                    });
                    const parsedData = {
                        ...tempScenarioInfo,
                        simulationList: scenariosList,
                    };
                    return parsedData;
                });
                return {};
            } catch (error) {
                throw error;
            }
        },
    });
    const dimensionSectionWidth = useRef<number>(defaultDimSectionWidth);

    const mainSectionHeight = useRef<number | null>(0);
    const dimSectionWidth = useRef<number | null>(0);
    const lensesSectionWidth = useRef<number | null>(0);

    //this hook with give height to fill the screen height
    mainSectionHeight.current = useFillHeightToFitScreen({
        id: 'sample-main-section',
        height: mainSectionHeight.current,
        buffer: 70, //for padding at the bottom, change to 16 once testing is done
        // triggerDummyRender: true,
    });
    //this hook with convert percentage to width in px and return
    dimSectionWidth.current = useConvertPercentageToPxWidth({
        id: 'scenario-main-section',
        width: dimSectionWidth.current,
        percentageNumber: dimensionSectionWidth.current,
        triggerDummyRender: true,
    });
    //this hook with convert percentage to width in px and return
    lensesSectionWidth.current = useConvertPercentageToPxWidth({
        id: 'scenario-main-section',
        width: lensesSectionWidth.current,
        percentageNumber: 100 - dimensionSectionWidth.current,
        // triggerDummyRender: true,
    });

    //resize section when more dims are added
    useMemo(() => {
        dimSectionWidth.current = 0;
        lensesSectionWidth.current = 0;
        dimensionSectionWidth.current = Math.max(
            defaultDimSectionWidth,
            Math.ceil(selectedDimensionListValues.length * 12),
        );
    }, [selectedDimensionListValues]);

    //update scenario api
    const { mutate: updateScenarioMutation, loading: scenarioNameUpdateLoading } = useCommonMutationHandler({
        url: pricingSimulatorUrls.updateScenarioUrl,
        method: 'post',
    });

    //simulate scenario api
    const { mutate: simulateScenarioMutation, loading: simulateScenarioLoading } = useCommonMutationHandler({
        url: pricingSimulatorUrls.simulateScenarioUrl,
        method: 'post',
    });

    //reset scenario api
    const { mutate: resetScenarioMutation, loading: resetScenarioLoading } = useCommonMutationHandler({
        url: pricingSimulatorUrls.resetScenarioUrl,
        method: 'post',
    });

    const updateScenarioNameHandler = (updatedName: string) => {
        const payload = {
            project_id: projectIdFromUrl,
            scenario_id: scenarioIdFromUrl,
            scenario_name: updatedName,
            user_name: localStorage.getItem('email'),
        };

        if (updatedName !== selectedScenarioInfo?.scenarioName) {
            updateScenarioMutation(
                { ...payload },
                {
                    onSuccess: (data) => {
                        updateScenarioProperty('scenarioName', updatedName);
                        const newScenarios = selectedProjectInfo?.simulationList?.map((item: Scenario) => {
                            return {
                                ...item,
                                scenarioName:
                                    Number(selectedScenarioInfo?.scenarioId) === item?.scenarioId
                                        ? updatedName
                                        : item?.scenarioName,
                            };
                        });
                        // updating scenario name in the simulation list
                        setSelectedProjectInfo({
                            ...selectedProjectInfo,
                            simulationList: newScenarios,
                        });
                    },
                    onError: (err: any) => {},
                },
            );
        } else {
            // temp name for handling change
            updateScenarioProperty('scenarioName', 'Temp Name');
            setTimeout(() => {
                updateScenarioProperty('scenarioName', updatedName);
            });
        }
    };

    // TODO: fetch project details loader
    const projectDetailsLoading = false;

    const { refetch } = useFetchScenarioStatus({
        updateScenarioProperty,
        selectedProjectInfo,
        setSelectedProjectInfo,
        refetchScenarioTableData,
        refetchLensConfigData,
        queryKey: [PricingSimulatorQueryEnum.SCENARIO_STATUS, scenarioIdFromUrl],
        variables: { scenario_id: scenarioIdFromUrl },
        queryConfig: { enabled: !!scenarioIdFromUrl },
    });

    // GET SCENARIO DETAILS
    const { loading: scenarioDetailsLoading } = useFetchScenarioDetails({
        method: 'get',
        queryConfig: {
            enabled: Boolean(hasProjectAccess) && Boolean(dim1DropdownList?.length > 0) && Boolean(scenarioIdFromUrl),
        },
        queryKey: [PricingSimulatorQueryEnum.SCENARIO_DETAILS, scenarioIdFromUrl],
        url: `${pricingSimulatorUrls.getSingleScenarioDetailsUrl}/${scenarioIdFromUrl}`,
        variables: {},
    });

    // Use useInterval to fetch status every 10 seconds
    useInterval(
        () => {
            refetch();
        },
        shouldFetch ? 10000 : null, // 10000ms = 10 seconds
    );

    const setUpOrInprogressLoading = pollingStatuses.includes(selectedScenarioInfo?.status || '');
    const isErrorStatus = errorStatuses.includes(selectedScenarioInfo?.status || '');
    const isActive = selectedScenarioInfo?.status === 'ACTIVE';
    useEffect(() => {
        const newStatus = selectedScenarioInfo?.status || 'z';
        if (![...pollingStatuses, 'z'].includes(newStatus)) {
            setShouldFetch(false);
        } else {
            setShouldFetch(true);
        }
    }, [selectedScenarioInfo, scenarioIdFromUrl]);

    const scenarioPerProjectLimitExceeded =
        (selectedProjectInfo?.simulationList?.length || 0) + 1 > scenarioPerProjectLimit;

    //create scenario api
    const { mutate: createScenarioMutation, loading: createScenarioLoading } = useCommonMutationHandler({
        url: pricingSimulatorUrls.createNewScenario,
        method: 'post',
    });

    //duplicate scenario api
    const { mutate: duplicateScenarioMutation, loading: duplicateScenarioLoading } = useCommonMutationHandler({
        url: pricingSimulatorUrls.duplicateScenario,
        method: 'post',
    });

    //called when clicked on duplicate scenario button
    const onDuplicateScenarioClick = () => {
        if (scenarioPerProjectLimitExceeded) return;
        const inputData = {
            project_id: selectedProjectInfo?.projectId,
            created_by: loggedInUser,
            scenario_id: selectedScenarioInfo?.scenarioId,
        };
        duplicateScenarioMutation(inputData, {
            onSuccess: (data) => {
                sendNotification('success', `Scenario ${selectedScenarioInfo?.scenarioName} has been duplicated`);
                const newUrl = `/pricing/recommendation/pricing-simulator/update/${selectedProjectInfo?.projectId}/scenario/${data?.data?.scenario_id}`;
                setSelectedProjectInfo({ ...selectedProjectInfo, simulationList: [] });
                navigate(newUrl);
                queryClient.refetchQueries({
                    exact: false,
                    queryKey: ['ps-single-project-details', 'ps-all-scenarios-details', projectIdFromUrl],
                });
                //window.location.href = window.location.origin + newUrl;
            },
            onError: (error: any) => {
                const errorMsg = generateErrorMsg(String(error?.response?.data?.detail));
                //show error message
                sendNotification('error', errorMsg);
            },
        });
    };

    const onCreateScenarioClick = () => {
        const inputData = { project_id: selectedProjectInfo?.projectId, created_by: loggedInUser };
        setShowIsEditedRecords(false); // resetting show edited records to false
        createScenarioMutation(inputData, {
            onSuccess: (data) => {
                sendNotification('success', PricingSimulatorNotificationMessages.CREATE_SCENARIO_SUCCESS);
                const newUrl = `/pricing/recommendation/pricing-simulator/update/${selectedProjectInfo?.projectId}/scenario/${data?.data?.scenario_id}`;
                setSelectedProjectInfo({ ...selectedProjectInfo, simulationList: [] });
                navigate(newUrl);
                queryClient.refetchQueries({
                    exact: false,
                    queryKey: ['ps-single-project-details', 'ps-all-scenarios-details', projectIdFromUrl],
                });
                updateScenarioProperty('status', 'SETUP');
                //window.location.href = window.location.origin + newUrl;
            },
            onError: (val: any) => {
                sendNotification('error', PricingSimulatorNotificationMessages.CREATE_SCENARIO_ERROR);
            },
        });
    };

    const createScenarioObj: any[] = [
        {
            label: (
                <div
                    className={`flex items-center ${
                        scenarioPerProjectLimitExceeded ? 'cursor-not-allowed  opacity-40' : ''
                    }`}
                    onClick={() => !scenarioPerProjectLimitExceeded && onCreateScenarioClick()}
                >
                    <FileAddOutlined style={{ fontSize: 18 }} /> <span>Create Scenario</span>
                </div>
            ),
            key: 'create_scenario',
        },
        // {
        //     label: (
        //         <div className="flex items-center">
        //             <FileAddOutlined style={{ fontSize: 18 }} /> <span>Compare Scenarios</span>
        //         </div>
        //     ),
        //     key: 'compare_scenarios',
        // },
    ];

    //called when dimension popup is closed
    function onDimensionPopupClose(tempSelectedDimensionList: AttributeTypeItem[]) {
        let tempDimensionList = tempSelectedDimensionList;
        let values = tempDimensionList?.map((list) => list.value);

        //if nothing is selected, default to dim1 - product
        if (!values?.length) {
            tempDimensionList = [defaultDim1DropdownItem];
            values = tempDimensionList.map((item) => item.value);
        }

        // return sendNotification('error', 'Minimum 2 Attributes should be selected');
        updateScenarioProperty('dimAttributes', tempDimensionList);
        updateScenarioProperty('offset', 0); //reset offset
        setDimensionCheckboxObj({}); // reset checkbox
        //update sort function if this column is active
        if (selectedDimensionListValues.includes(selectedScenarioInfo?.sortObject?.sortBy || '')) {
            updateScenarioProperty('sortObject', {
                sortBy: values?.includes(selectedScenarioInfo?.sortObject?.sortBy ?? '')
                    ? selectedScenarioInfo?.sortObject?.sortBy ?? ''
                    : values?.[0],
                sortingOrder: selectedScenarioInfo?.sortObject?.sortingOrder || 'asc',
                sortType: 'actual',
            });
        }
    }

    const commonLoading =
        projectAccessLoading ||
        lenseConfigLoading ||
        allScenariosLoading ||
        singleProjectDetailsLoading ||
        simulateScenarioLoading ||
        scenarioDataLoading ||
        createScenarioLoading ||
        duplicateScenarioLoading ||
        resetScenarioLoading ||
        scenarioDetailsLoading;

    //if polling is active then show "inProgress" status for footer and update footer message
    const footerFirstText =
        selectedScenarioInfo?.status === 'IN_PROGRESS'
            ? 'Simulation is in-Progress...'
            : selectedScenarioInfo?.status === 'SETUP'
            ? 'Setting up scenario...'
            : selectedScenarioInfo?.data?.length > 0
            ? `Showing ${selectedScenarioInfo?.data?.length?.toLocaleString()} of ${selectedScenarioInfo?.count?.toLocaleString()} records`
            : '';
    const footerFourthText = selectedScenarioInfo?.updatedAt ? `Last Updated: ${selectedScenarioInfo?.updatedAt}` : '';
    const footerThirdText = selectedScenarioInfo?.createdBy ? `Created by: ${selectedScenarioInfo?.createdBy}` : '';
    const footerSecondText = errorStatuses.includes(selectedScenarioInfo?.status || '')
        ? 'Reload scenario from simulation projects page'
        : '';
    const isNameAlreadyExists = useMemo(() => {
        return selectedProjectInfo?.simulationList
            ?.filter((scen: Scenario) => scen.scenarioId !== selectedScenarioInfo?.scenarioId)
            .map((sc: Scenario) => sc.scenarioName)
            ?.includes(tempName);
    }, [tempName, scenarioId]);

    return (
        <>
            {projectDetailsLoading ? (
                <div style={{ height: chartHeight }} className="bg-white flex justify-center items-center">
                    <Spin />
                </div>
            ) : (
                <section style={{ height: chartHeight }} id="pricing-simulator-update-view-page">
                    <div className="flex items-baseline justify-between gap-4">
                        <Breadcrumb style={{ marginBottom: 10, flexShrink: 0 }} items={breadcrumbItems} />
                        {/* SCENARIO DETAILS */}
                        <ProjectOverviewContainer />
                    </div>
                    <div>
                        {/* SCENARIO TABS */}
                        <TabsContainer
                            activeKey={scenarioId?.toString()}
                            setActiveKey={updateSelectedScenarioId}
                            items={
                                selectedProjectInfo?.simulationList?.map((sc: Scenario) => {
                                    return {
                                        label: sc.scenarioName,
                                        key: sc.scenarioId?.toString(),
                                        dataKey: sc.scenarioId?.toString(),
                                    };
                                }) || []
                            }
                            customTabsObj={createScenarioObj}
                        />
                        {/* MAIN PANEL */}
                        <div className="bg-white p-2 pb-0 border-x border-x-borderLinePurpuleAlt border-y-0">
                            <div className="p-2 gap-2 flex flex-col  border-b border-b-borderLinePurpuleAlt">
                                <div className="w-full flex items-center justify-between">
                                    <div className="font-sm font-medium text-blackAlt flex gap-2 items-center">
                                        <InputDisplay
                                            inputValue={selectedScenarioInfo?.scenarioName || ''}
                                            defaultDisplayName={selectedScenarioInfo?.scenarioName || 'Scenario Name'}
                                            maxCharacter={50}
                                            maxLengthValidationMessage={
                                                pricingSimulatorErrorMessages.MAX_LENGTH_PRICING_SIMULATOR
                                            }
                                            minLengthValidationMessage={priceSimulatorErrorMessages.MIN_LENGTH}
                                            alreadyExistMessage={priceSimulatorErrorMessages.DUPLICATE_NAME}
                                            alreadyExist={isNameAlreadyExists}
                                            disable={viewMode}
                                            minCharacter={3}
                                            onApplyHandler={(updatedName: string) => {
                                                updateScenarioNameHandler(updatedName);
                                            }}
                                            textChangeHandler={(value: string) => setTempName(value)}
                                            showLoader={scenarioNameUpdateLoading}
                                            disableBlur
                                        />
                                        {/* BULK UPLOAD TAG COMPONENT */}
                                        <ScenarioTag />
                                    </div>
                                    {/* ALL THE SCENARIO ACTIONS COMPONENT */}
                                    <ScenarioActions
                                        onDuplicateScenarioClick={onDuplicateScenarioClick}
                                        commonLoader={commonLoading}
                                        resetScenarioMutation={resetScenarioMutation}
                                        simulateScenarioMutation={simulateScenarioMutation}
                                    />
                                </div>
                                <FilterRibbon
                                    allCheckedAttribute={allCheckedAttribute}
                                    allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                                    applyFilterObject={applyFilterObject}
                                    resetFilterObject={resetFilterObject}
                                    showIsEditedRecords={showIsEditedRecords}
                                    onSubmitHandler={(obj: any) => {
                                        setAllCheckedAttribute(obj.allCheckedAttribute);
                                        setAllSelectedValueForAllAttribute(obj.allSelectedValueForAllAttribute);
                                        setApplyFilterObject(obj.applyFilterObject);
                                        updateScenarioProperty('offset', 0);
                                        saveFilterConfig(obj);
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    {/* MAIN SECTION */}

                    <div
                        className="flex flex-col"
                        id="sample-main-section"
                        style={{ height: mainSectionHeight.current ? mainSectionHeight.current : 'auto' }}
                    >
                        {setUpOrInprogressLoading ? (
                            <div
                                className="relative flex flex-col items-center bg-white "
                                style={{
                                    height: mainSectionHeight.current || 'auto',
                                }}
                            >
                                {/* {setUpOrInprogressLoading && (
                                    <div className="absolute top-[55%]">
                                        Scenario is in {selectedScenarioInfo?.status} state. Please check after
                                        sometime.
                                    </div>
                                )} */}
                                <Loader
                                    loaderMessage={`Scenario is ${
                                        selectedScenarioInfo?.status === 'SETUP' ? 'in' : ''
                                    } ${
                                        projectStatuses?.[selectedScenarioInfo?.status || '']?.label
                                    } state. Please check after
                                        sometime.`}
                                />
                            </div>
                        ) : (
                            <>
                                <div
                                    id="scenario-main-section"
                                    className=" flex bg-white border-x border-x-borderLinePurpuleAlt"
                                >
                                    <div
                                        style={{ minWidth: `${defaultDimSectionWidth}%` || 'auto' }}
                                        className="border-r border-r-fontGreyAlt1 pl-2 pt-2"
                                    >
                                        <DimSection
                                            onPopupClose={onDimensionPopupClose}
                                            dimensionInfo={{
                                                activeLense: selectedScenarioInfo?.activeLense,
                                                dimAttributes: selectedScenarioInfo?.dimAttributes ?? [],
                                            }}
                                            selectedDimensionListValues={selectedDimensionListValues}
                                        />
                                    </div>
                                    <div style={{ width: `${100 - defaultDimSectionWidth}%` || 'auto' }}>
                                        <LenseSection />
                                    </div>
                                </div>
                                {/* scenario table  */}
                                <ScenarioTableWrapper
                                    commonLoader={commonLoading}
                                    widthConfig={{
                                        dimSection: dimSectionWidth.current || 'auto',
                                        lensesSection: lensesSectionWidth.current || 'auto',
                                    }}
                                />
                            </>
                        )}
                        <Footer
                            type={
                                isErrorStatus
                                    ? 'error'
                                    : setUpOrInprogressLoading
                                    ? 'inProgress'
                                    : isActive
                                    ? 'success'
                                    : 'default'
                            }
                            status={
                                setUpOrInprogressLoading
                                    ? null
                                    : projectStatuses?.[selectedScenarioInfo?.status || '']?.label
                            }
                            items={[footerFirstText, footerSecondText, footerThirdText, footerFourthText]}
                        />
                    </div>
                </section>
            )}
        </>
    );
};
export default UpdateOrViewPricingSimulator;
